/**
 * Monthly GST sales register (campus sales) — har mahine GST wale ko dene ke liye.
 * Sticker / interview cards jaisa hi: button dabao → PDF seedha download.
 *
 * Aankde `computeBookingGst` se aate hain — wahi function jo customer ka tax
 * invoice chhapta hai. Isliye register ka har number us invoice se milta hai
 * jo customer ko diya gaya tha (GST filing me yahi sabse zaroori baat hai).
 *
 * Ek bill me do GST rate ho sakte hain (item ka share 2500 se upar = 18%,
 * warna 5%), isliye row RATE-WISE hoti hai: mixed bill ki do rows banti hain
 * — GST return bhi rate-wise hi maangta hai.
 */
import { jsPDF } from "jspdf";
import { computeBookingGst, FIRM } from "./campus_invoice";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const num2 = (v) => Number(v || 0).toFixed(2);
const titleCase = (s) =>
    String(s || "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) || "—";

/** Invoice ki tareekh — GST me isi se mahina tay hota hai. */
export const billDateOf = (b) => String(b.order_date || b.created_at || "").slice(0, 10);

const isCancelled = (b) => String(b.status || "").toLowerCase() === "cancelled";

/** "2026-07" → us mahine ke bills (cancelled chhod ke), invoice no. se sorted. */
export function bookingsForMonth(bookings, ym) {
    return (bookings || [])
        .filter((b) => !isCancelled(b))
        .filter((b) => String(b.invoice_no || "").trim())
        .filter((b) => billDateOf(b).slice(0, 7) === ym)
        .sort((a, b) => String(a.invoice_no).localeCompare(String(b.invoice_no), undefined, { numeric: true }));
}

/** Jin mahino me bills hain unki list (naya pehle) — dropdown ke liye. */
export function monthsWithBills(bookings) {
    const set = new Set();
    (bookings || []).forEach((b) => {
        if (isCancelled(b) || !String(b.invoice_no || "").trim()) return;
        const ym = billDateOf(b).slice(0, 7);
        if (ym) set.add(ym);
    });
    return [...set].sort().reverse();
}

export const monthLabel = (ym) => {
    const [y, m] = String(ym || "").split("-");
    const mi = parseInt(m, 10) - 1;
    return MONTHS[mi] ? `${MONTHS[mi]} ${y}` : ym;
};

/** Register ki rows + saare totals. PDF aur on-screen dono isi se bante hain. */
export function buildRegister(bookings, ym) {
    const list = bookingsForMonth(bookings, ym);
    const rows = [];
    const byRate = new Map();   // rate → { taxable, cgst, sgst, igst, gross, bills:Set }
    const byState = new Map();  // state → Map(rate → { taxable, cgst, sgst, igst, gross })
    let sno = 0;

    list.forEach((b) => {
        const g = computeBookingGst(b);
        const d = billDateOf(b);
        const [yy, mm, dd] = d.split("-");
        const dateStr = dd ? `${dd}.${mm}.${yy}` : d;

        g.rateRows.forEach((r) => {
            sno += 1;
            rows.push({
                sno,
                billNo: b.invoice_no || "—",
                date: dateStr,
                taxable: r.taxable,
                rate: r.rate,
                cgst: r.cgst,
                sgst: r.sgst,
                igst: r.igst,
                gross: r.gross,
                gstin: String(b.customer_gstin || "").trim(),  // campus sales aam taur pe B2C
                party: titleCase(b.customer_name),
                state: g.buyerState,
            });

            const acc = byRate.get(r.rate) || { rate: r.rate, taxable: 0, cgst: 0, sgst: 0, igst: 0, gross: 0, bills: new Set() };
            acc.taxable += r.taxable; acc.cgst += r.cgst; acc.sgst += r.sgst; acc.igst += r.igst; acc.gross += r.gross;
            acc.bills.add(b.invoice_no);
            byRate.set(r.rate, acc);

            if (!byState.has(g.buyerState)) byState.set(g.buyerState, new Map());
            const sm = byState.get(g.buyerState);
            const sAcc = sm.get(r.rate) || { rate: r.rate, taxable: 0, cgst: 0, sgst: 0, igst: 0, gross: 0 };
            sAcc.taxable += r.taxable; sAcc.cgst += r.cgst; sAcc.sgst += r.sgst; sAcc.igst += r.igst; sAcc.gross += r.gross;
            sm.set(r.rate, sAcc);
        });
    });

    const totals = rows.reduce((t, r) => ({
        taxable: t.taxable + r.taxable,
        cgst: t.cgst + r.cgst,
        sgst: t.sgst + r.sgst,
        igst: t.igst + r.igst,
        gross: t.gross + r.gross,
    }), { taxable: 0, cgst: 0, sgst: 0, igst: 0, gross: 0 });

    const rateSummary = [...byRate.values()]
        .sort((a, b) => a.rate - b.rate)
        .map((r) => ({ ...r, billCount: r.bills.size }));

    const stateSummary = [...byState.entries()]
        .map(([state, m]) => ({
            state,
            rates: [...m.values()].sort((a, b) => a.rate - b.rate),
        }))
        .sort((a, b) => a.state.localeCompare(b.state));

    return { rows, totals, rateSummary, stateSummary, billCount: list.length };
}

// ── PDF layout (A4 landscape, mm) ──
const PAGE_W = 297;
const PAGE_H = 210;
const M = 8;                       // side margin
const COLS = [
    { key: "sno", label: "S.NO.", w: 11, align: "center" },
    { key: "billNo", label: "BILL NO.", w: 23, align: "left" },
    { key: "date", label: "DATE", w: 21, align: "center" },
    { key: "taxable", label: "BILL AMOUNT", w: 25, align: "right", money: true },
    { key: "rate", label: "GST%", w: 13, align: "center", pct: true },
    { key: "cgst", label: "CGST", w: 22, align: "right", money: true },
    { key: "sgst", label: "SGST", w: 22, align: "right", money: true },
    { key: "igst", label: "IGST", w: 21, align: "right", money: true },
    { key: "gross", label: "TOTAL BILL", w: 26, align: "right", money: true },
    { key: "gstin", label: "GSTIN", w: 26, align: "left" },
    { key: "party", label: "PARTY NAME", w: 47, align: "left" },
    { key: "state", label: "PLACE OF SUPPLY", w: 24, align: "left" },
];
const TABLE_W = COLS.reduce((a, c) => a + c.w, 0);
const ROW_H = 5;
const HEAD_H = 6;

const cellText = (doc, text, x, y, w, align) => {
    const s = String(text ?? "");
    const pad = 1.2;
    if (align === "right") doc.text(s, x + w - pad, y, { align: "right" });
    else if (align === "center") doc.text(s, x + w / 2, y, { align: "center" });
    else doc.text(doc.splitTextToSize(s, w - pad * 2)[0] || "", x + pad, y);
};

const drawTableHead = (doc, y) => {
    doc.setFillColor(225, 228, 232);
    doc.rect(M, y, TABLE_W, HEAD_H, "F");
    doc.setDrawColor(120);
    doc.setLineWidth(0.15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.4);
    doc.setTextColor(20);
    let x = M;
    COLS.forEach((c) => {
        doc.rect(x, y, c.w, HEAD_H);
        cellText(doc, c.label, x, y + HEAD_H - 2, c.w, c.align === "right" ? "right" : "center");
        x += c.w;
    });
    return y + HEAD_H;
};

const pageHeader = (doc, ym, pageNo) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(15);
    doc.text(`GST FOR MONTH OF ${monthLabel(ym)}`, PAGE_W / 2, 10, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(60);
    doc.text(`${FIRM.legalName}  (${FIRM.brand})   ·   GSTIN: ${FIRM.gstin}   ·   State: ${FIRM.state} (${FIRM.stateCode})`, PAGE_W / 2, 15, { align: "center" });
    doc.setFontSize(6.5);
    doc.setTextColor(110);
    doc.text("Campus sales register  ·  amounts are GST-inclusive, split rate-wise", M, 19.5);
    doc.text(`Page ${pageNo}`, PAGE_W - M, 19.5, { align: "right" });
    return 21.5;
};

/** @param {Array} bookings @param {string} ym  e.g. "2026-07" */
export function generateGstRegisterPDF(bookings, ym) {
    const reg = buildRegister(bookings, ym);
    if (!reg.rows.length) {
        alert(`No campus bills found for ${monthLabel(ym)}.`);
        return;
    }

    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape", compress: true });
    let pageNo = 1;
    let y = pageHeader(doc, ym, pageNo);
    y = drawTableHead(doc, y);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.6);
    doc.setTextColor(25);

    const bottomLimit = PAGE_H - 12;
    reg.rows.forEach((r, i) => {
        if (y + ROW_H > bottomLimit) {
            doc.addPage("a4", "landscape");
            pageNo += 1;
            y = pageHeader(doc, ym, pageNo);
            y = drawTableHead(doc, y);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(6.6);
            doc.setTextColor(25);
        }
        if (i % 2 === 1) {
            doc.setFillColor(246, 247, 249);
            doc.rect(M, y, TABLE_W, ROW_H, "F");
        }
        doc.setDrawColor(190);
        doc.setLineWidth(0.1);
        let x = M;
        COLS.forEach((c) => {
            doc.rect(x, y, c.w, ROW_H);
            const raw = r[c.key];
            const txt = c.money ? num2(raw) : c.pct ? `${raw}%` : (raw || (c.key === "gstin" ? "—" : ""));
            cellText(doc, txt, x, y + ROW_H - 1.6, c.w, c.align);
            x += c.w;
        });
        y += ROW_H;
    });

    // ── TOTAL row ──
    if (y + ROW_H + 2 > bottomLimit) {
        doc.addPage("a4", "landscape");
        pageNo += 1;
        y = pageHeader(doc, ym, pageNo);
        y = drawTableHead(doc, y);
    }
    doc.setFillColor(20, 22, 26);
    doc.rect(M, y, TABLE_W, ROW_H + 1, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(255);
    let tx = M;
    COLS.forEach((c) => {
        let txt = "";
        if (c.key === "billNo") txt = "TOTAL";
        else if (c.key === "sno") txt = "";
        else if (c.key === "taxable") txt = num2(reg.totals.taxable);
        else if (c.key === "cgst") txt = num2(reg.totals.cgst);
        else if (c.key === "sgst") txt = num2(reg.totals.sgst);
        else if (c.key === "igst") txt = num2(reg.totals.igst);
        else if (c.key === "gross") txt = num2(reg.totals.gross);
        else if (c.key === "party") txt = `${reg.billCount} bills`;
        cellText(doc, txt, tx, y + ROW_H - 1.2, c.w, c.align);
        tx += c.w;
    });
    y += ROW_H + 5;

    // ── Summary page: rate-wise + state-wise (B2C filing ke liye) ──
    doc.addPage("a4", "landscape");
    pageNo += 1;
    y = pageHeader(doc, ym, pageNo);
    y += 2;

    const sumCols = [
        { label: "GST%", w: 18, align: "center" },
        { label: "BILLS", w: 18, align: "center" },
        { label: "TAXABLE VALUE", w: 34, align: "right" },
        { label: "CGST", w: 28, align: "right" },
        { label: "SGST", w: 28, align: "right" },
        { label: "IGST", w: 28, align: "right" },
        { label: "TOTAL GST", w: 30, align: "right" },
        { label: "INVOICE VALUE", w: 34, align: "right" },
    ];
    const sumW = sumCols.reduce((a, c) => a + c.w, 0);

    const drawSumHead = (title, cols, width) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(15);
        doc.text(title, M, y);
        y += 2.5;
        doc.setFillColor(225, 228, 232);
        doc.rect(M, y, width, HEAD_H, "F");
        doc.setDrawColor(120);
        doc.setLineWidth(0.15);
        doc.setFontSize(6.4);
        let hx = M;
        cols.forEach((c) => {
            doc.rect(hx, y, c.w, HEAD_H);
            cellText(doc, c.label, hx, y + HEAD_H - 2, c.w, "center");
            hx += c.w;
        });
        y += HEAD_H;
    };

    drawSumHead("RATE-WISE SUMMARY", sumCols, sumW);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.setTextColor(25);
    reg.rateSummary.forEach((r) => {
        doc.setDrawColor(190);
        doc.setLineWidth(0.1);
        const vals = [`${r.rate}%`, String(r.billCount), num2(r.taxable), num2(r.cgst), num2(r.sgst), num2(r.igst), num2(r.cgst + r.sgst + r.igst), num2(r.gross)];
        let sx = M;
        sumCols.forEach((c, i) => {
            doc.rect(sx, y, c.w, ROW_H);
            cellText(doc, vals[i], sx, y + ROW_H - 1.6, c.w, c.align);
            sx += c.w;
        });
        y += ROW_H;
    });
    // rate total
    doc.setFillColor(20, 22, 26);
    doc.rect(M, y, sumW, ROW_H + 1, "F");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255);
    doc.setFontSize(7);
    {
        const t = reg.totals;
        const vals = ["TOTAL", String(reg.billCount), num2(t.taxable), num2(t.cgst), num2(t.sgst), num2(t.igst), num2(t.cgst + t.sgst + t.igst), num2(t.gross)];
        let sx = M;
        sumCols.forEach((c, i) => {
            cellText(doc, vals[i], sx, y + ROW_H - 1.2, c.w, c.align);
            sx += c.w;
        });
    }
    y += ROW_H + 7;

    // state-wise (place of supply) — B2C others isi hisaab se bharte hain
    const stCols = [
        { label: "PLACE OF SUPPLY", w: 44, align: "left" },
        { label: "GST%", w: 18, align: "center" },
        { label: "TAXABLE VALUE", w: 34, align: "right" },
        { label: "CGST", w: 28, align: "right" },
        { label: "SGST", w: 28, align: "right" },
        { label: "IGST", w: 28, align: "right" },
        { label: "INVOICE VALUE", w: 34, align: "right" },
    ];
    const stW = stCols.reduce((a, c) => a + c.w, 0);
    drawSumHead("STATE-WISE (PLACE OF SUPPLY) SUMMARY", stCols, stW);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.setTextColor(25);
    reg.stateSummary.forEach((s) => {
        s.rates.forEach((r, ri) => {
            if (y + ROW_H > PAGE_H - 12) {
                doc.addPage("a4", "landscape");
                pageNo += 1;
                y = pageHeader(doc, ym, pageNo) + 2;
                drawSumHead("STATE-WISE (PLACE OF SUPPLY) SUMMARY (contd.)", stCols, stW);
                doc.setFont("helvetica", "normal");
                doc.setFontSize(6.8);
                doc.setTextColor(25);
            }
            doc.setDrawColor(190);
            doc.setLineWidth(0.1);
            const vals = [ri === 0 ? s.state : "", `${r.rate}%`, num2(r.taxable), num2(r.cgst), num2(r.sgst), num2(r.igst), num2(r.gross)];
            let sx = M;
            stCols.forEach((c, i) => {
                doc.rect(sx, y, c.w, ROW_H);
                cellText(doc, vals[i], sx, y + ROW_H - 1.6, c.w, c.align);
                sx += c.w;
            });
            y += ROW_H;
        });
    });

    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(110);
    doc.text(
        "Note: Campus sales are B2C (unregistered buyers) — GSTIN column blank. Prices are GST-inclusive; taxable value is back-calculated. "
        + "A bill with items on two rates appears as two rows (rate-wise), so bill numbers may repeat.",
        M, y, { maxWidth: TABLE_W }
    );

    doc.save(`GST-campus-${monthLabel(ym).replace(" ", "-")}.pdf`);
}

export default generateGstRegisterPDF;
